import { type ChangeEvent, useEffect, useRef, type JSX, createRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { Word } from "./Word"
import { pushPrestripIndex, setInputWords, startHome, popPrestripIndex, resetHome } from "./homeSlice"
import { nonAlphabetLanguages } from "@/lib/utils"

export const Words = (): JSX.Element | null => {
  const homeSlice = useAppSelector(state => state.home)
  const dispatch = useAppDispatch()

  const [isComposing, setIsComposing] = useState(false)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const isFocusedRef = useRef(false)
  const [isFocused, setIsFocused] = useState(false)
  const wordRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])

  const foreignLanguageIsNonAlphabetOrNativeWhenMixedMode =
    (nonAlphabetLanguages.includes(homeSlice.foreignLanguage) && homeSlice.strictMode) ||
    (nonAlphabetLanguages.includes(homeSlice.nativeLanguage) && homeSlice.mixedMode)

  //   const isForeignLanguageAlphabet = !nonAlphabetLanguages.includes(homeSlice.foreignLanguage)

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const inputValue = event.currentTarget.value

    // reset game on empty input field
    if (inputValue.length === 0 && homeSlice.gameState === "running") {
      dispatch(resetHome())
      return
    }

    // start game
    if (homeSlice.gameState === "ready" && inputValue.length > 0) dispatch(startHome(Date.now()))

    if (inputValue.trim().length > 0) dispatch(setInputWords({ input: inputValue, time: Date.now() }))
  }

  // note: not triggered right after focus
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const { code } = event
    // console.log(code, event.key)

    if (code === "Space") {
      // prevent double spaces and spaces before other inputs
      const endsWithWhitespace = /\s+$/.exec(event.currentTarget.value) !== null
      if (endsWithWhitespace || event.currentTarget.value.length === 0) {
        event.preventDefault()
        return
      }
      checkNewLine()
    }

    if (code === "Backspace") {
      checkOldLine()
    }

    // event.currentTarget.value = event.currentTarget.value.replace(/ +/g, " ") // remove double spaces
  }

  // check if the current word is on a new line, if so, set prestrip index
  function checkNewLine() {
    const currWord = wordRefs.current[homeSlice.wordIndex]?.current?.getBoundingClientRect()
    const prevWord = wordRefs.current[homeSlice.wordIndex + 1]?.current?.getBoundingClientRect()

    if (currWord && prevWord && currWord.top !== prevWord.top) {
      dispatch(pushPrestripIndex(homeSlice.wordIndex))
    }
  }

  function checkOldLine() {
    const currWord = wordRefs.current[homeSlice.wordIndex]?.current?.getBoundingClientRect()
    const prevWord = wordRefs.current[homeSlice.wordIndex - 1]?.current?.getBoundingClientRect()

    if (
      currWord &&
      prevWord &&
      currWord.top !== prevWord.top &&
      homeSlice.inputWords[homeSlice.inputWords.length - 1].length === 0 // space -> always at the end of the word; backspace -> not always at the start
    ) {
      dispatch(popPrestripIndex())
    }
  }

  // create useRef for each word and focus on input field on mount
  useEffect(() => {
    wordRefs.current = Array.from({ length: homeSlice.foreignWords.length }, () => createRef<HTMLDivElement>())

    function focusInputField(event: KeyboardEvent) {
      if (!isFocusedRef.current) event.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener("keydown", focusInputField)
    return () => {
      window.removeEventListener("keydown", focusInputField)
    }
  }, [])

  // make sure input field is empty when game gets restarted
  useEffect(() => {
    console.log(isComposing) // just to prevent annoying eslint warning
    if (homeSlice.gameState === "ready" && inputRef.current) inputRef.current.value = ""
  }, [homeSlice.gameState])

  return (
    // <div className="w-4/5 bg-gray-900 rounded-3xl p-16 select-none break-all text-center">
    <div
      // text should match bg to hide ellipses
      className={`w-4/5 bg-gray-800 text-gray-800 text-2xl rounded-3xl shadow-md p-16 select-none break-all text-center`}
      onClick={() => inputRef.current?.focus()}
    >
      {/* text that shows on blurred words component */}
      {!isFocused && (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white leading-loose my-2 z-20">
          press any key or click here
        </div>
      )}
      <div
        className={`inline-block ${isFocused ? "" : "blur-xs"}`}
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          WebkitLineClamp: 3, // Adjust the number of lines as needed
        }}
      >
        {homeSlice.foreignWords.map((_word, index) => (
          <div key={index} className="border border-amber-600 border-none inline-block">
            <Word wordIndex={index} ref={wordRefs.current[index]}></Word>
          </div>
        ))}
      </div>
      <div className="flex justify-center">
        <input // TODO (idea): what if the input field is like the native word window, but below the foreign word. ergo it has it's own react component. think about it. could house foreign and native inputs
          className={`p-0 m-0 absolute text-white bg-gray-700 rounded-md ${foreignLanguageIsNonAlphabetOrNativeWhenMixedMode ? "" : "opacity-0"}`}
          // style={{ // this is the implementation for the TODO above
          //   left: getRect()?.left,
          //   top: getRect()?.bottom,
          //   width: getRect()?.width,
          // }}
          type="text"
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={event => {
            setIsComposing(false)
            if (homeSlice.foreignLanguage === "ja") {
              event.currentTarget.value = event.currentTarget.value + " "
              dispatch(setInputWords({ input: event.currentTarget.value, time: Date.now() }))
            }
          }}
          onFocus={() => {
            isFocusedRef.current = true
            setIsFocused(true)
          }}
          onBlur={() => {
            isFocusedRef.current = false
            setIsFocused(false)
          }}
          onPaste={e => e.preventDefault()}
          ref={inputRef}
          autoFocus
        ></input>
      </div>
    </div>
  )
}
